import type { ReactNode } from 'react';

/**
 * Loading placeholders — BUILD-PLAN §41, §80.
 *
 * An API-driven interface spends real time waiting, and what it shows in that
 * time is not a detail. A blank gap reads as broken; a centred spinner reads
 * as slow and tells the eye nothing about what is coming. A shape that
 * matches the content settles the layout before the data lands, so nothing
 * jumps when it does, and the customer's eye is already where the answer will
 * appear.
 *
 * Three rules hold across everything here:
 *
 *   1. **Match the real thing.** A skeleton whose proportions differ from the
 *      content it replaces causes a visible jolt on arrival — worse than no
 *      skeleton, because the page appears to break at the moment it succeeds.
 *   2. **Announce, do not narrate.** One `aria-busy` region per load. A
 *      screen reader should hear "loading" once, not a description of forty
 *      grey rectangles.
 *   3. **Respect reduced motion.** The shimmer is decoration; the shape is
 *      the information. Motion is dropped without losing meaning.
 */

/** A single bar. Width and height are the caller's business. */
export function Skeleton({ className = '' }: { className?: string }) {
  return (
    <span
      aria-hidden="true"
      className={[
        /*
         | A tint of the INK colour, never a surface token.
         |
         | The hand-rolled skeletons this component replaced painted
         | `bg-surface-muted` — which the theme resolves to #f9f0e7 against a
         | #f8efe6 background. One value per channel apart. They rendered,
         | they animated, and they were perfectly invisible, so the app
         | appeared to have no loading states while in fact having them
         | everywhere. Deriving the colour from the text colour means it
         | cannot collide with the background whatever the brand palette is
         | configured to.
         */
        'block rounded-sm bg-ink/[0.09]',
        // Suppressed under prefers-reduced-motion by the utility itself.
        'animate-pulse',
        className,
      ]
        .filter(Boolean)
        .join(' ')}
    />
  );
}

/**
 * Wraps a loading region so assistive technology hears it once.
 *
 * `aria-busy` on the container, and the skeletons inside marked
 * `aria-hidden` — otherwise a screen reader walks every placeholder.
 */
export function SkeletonRegion({
  label = 'Loading',
  children,
}: {
  label?: string;
  children: ReactNode;
}) {
  return (
    <div role="status" aria-busy="true" aria-label={label} className="contents">
      {children}
      <span className="sr-only">{label}…</span>
    </div>
  );
}

/**
 * A product card placeholder.
 *
 * Deliberately 3:4, matching the real card's image ratio — the commonest
 * cause of layout shift on a shop page is a square placeholder replaced by a
 * portrait photograph.
 */
export function ProductCardSkeleton() {
  return (
    <div className="flex flex-col gap-3">
      {/* Must track ProductCard's own crop exactly — square on phones, 3:4
          from `sm`. A skeleton with a different ratio makes the whole grid
          jump at the moment the products arrive. */}
      <Skeleton className="aspect-square w-full sm:aspect-[3/4]" />
      <Skeleton className="h-3.5 w-3/4" />
      <Skeleton className="h-3 w-1/3" />
      <Skeleton className="h-5 w-4/5" />
    </div>
  );
}

/** A grid of them, for a shop or collection listing. */
export function ProductGridSkeleton({ count = 8 }: { count?: number }) {
  return (
    <SkeletonRegion label="Loading products">
      <div className="grid grid-cols-2 gap-x-5 gap-y-10 lg:grid-cols-3 xl:grid-cols-4">
        {Array.from({ length: count }, (_, index) => (
          <ProductCardSkeleton key={index} />
        ))}
      </div>
    </SkeletonRegion>
  );
}

/** Gallery, title, price, options, button — the product page's real shape. */
export function ProductDetailSkeleton() {
  return (
    <SkeletonRegion label="Loading product">
      <div className="grid gap-10 lg:grid-cols-2 lg:gap-16">
        <Skeleton className="aspect-square w-full" />

        <div className="flex flex-col gap-5">
          <Skeleton className="h-3 w-24" />
          <Skeleton className="h-9 w-4/5" />
          <Skeleton className="h-6 w-28" />

          <div className="mt-2 flex flex-col gap-2.5">
            <Skeleton className="h-3 w-full" />
            <Skeleton className="h-3 w-full" />
            <Skeleton className="h-3 w-2/3" />
          </div>

          <div className="mt-4 flex gap-3">
            <Skeleton className="h-11 w-24" />
            <Skeleton className="h-11 w-24" />
            <Skeleton className="h-11 w-24" />
          </div>

          <Skeleton className="mt-4 h-12 w-full" />
        </div>
      </div>
    </SkeletonRegion>
  );
}

/** Lines of a cart or order summary, with a heavier total at the end. */
export function SummarySkeleton({ rows = 3 }: { rows?: number }) {
  return (
    <SkeletonRegion label="Loading summary">
      <div className="flex flex-col gap-3">
        {Array.from({ length: rows }, (_, index) => (
          <div key={index} className="flex items-center justify-between gap-4">
            <Skeleton className="h-3 w-2/5" />
            <Skeleton className="h-3 w-16" />
          </div>
        ))}

        <div className="mt-3 flex items-center justify-between gap-4 border-t border-line pt-4">
          <Skeleton className="h-3 w-16" />
          <Skeleton className="h-6 w-24" />
        </div>
      </div>
    </SkeletonRegion>
  );
}

/** Stacked lines, for a list of orders or addresses. */
export function ListSkeleton({ rows = 3 }: { rows?: number }) {
  return (
    <SkeletonRegion label="Loading">
      <div className="flex flex-col gap-4">
        {Array.from({ length: rows }, (_, index) => (
          <div key={index} className="flex flex-col gap-2.5 border border-line p-5">
            <Skeleton className="h-3.5 w-1/3" />
            <Skeleton className="h-3 w-1/2" />
            <Skeleton className="h-3 w-1/4" />
          </div>
        ))}
      </div>
    </SkeletonRegion>
  );
}

/**
 * The payment panel, mid-load.
 *
 * Given its own shape because it appears where a customer is most anxious —
 * money has been committed and they are waiting to be told what happens next.
 * A blank space there is the worst place in the whole journey to have one.
 */
export function PaymentPanelSkeleton() {
  return (
    <SkeletonRegion label="Loading payment options">
      <div className="border border-line bg-surface p-6 sm:p-7">
        <Skeleton className="h-6 w-52" />
        <Skeleton className="mt-3 h-3 w-40" />

        <div className="mt-6 flex flex-col gap-3">
          <Skeleton className="h-14 w-full" />
          <Skeleton className="h-14 w-full" />
        </div>

        <Skeleton className="mt-6 h-12 w-full" />
      </div>
    </SkeletonRegion>
  );
}
